import { useCallback, useEffect, useRef, useState } from 'react';
import {
  findBlock,
  updateBlockInTree,
  insertBlockAfter,
  flattenBlocks,
  createBlock,
  hasVariablesBlock,
  limitVariablesBlocks,
} from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import { stripHtml } from '../utils/caretUtils';
import {
  readBlocksFromClipboardHtml,
} from '../utils/blockClipboard';
import { clipboardToBlocks, writeSelectionToClipboardData } from '../utils/pasteUtils';
import { escapeAttr, escapeHtml } from '../utils/pasteUtils/htmlEscape';
import { separateNextUndoStep } from '../utils/undoSeparation';
import { ensureEditableCaretForInsert } from '../utils/atomicCaret';
import { EDITABLE_TYPES } from '../utils/editorConstants';
import {
  createLocalImageBlock,
  imageFilesFromDataTransfer,
} from '../utils/imageFiles';
import { startDocImageUpload } from '../utils/docImageUploadBridge';
import {
  isSingleVideoUrl,
  parseVideoUrl,
  stripVideoPasteChoiceAnchor,
  VIDEO_PASTE_MARK,
} from '../utils/videoEmbed';
import {
  createLocalVideoBlock,
  createVideoEmbedBlock,
  videoFilesFromDataTransfer,
} from '../utils/videoFiles';
import { startDocVideoUpload } from '../utils/docVideoUploadBridge';
import { VideoPasteChoice } from '../components/VideoPasteChoice';

/**
 * Editor-level paste (capture phase): image files, internal block JSON, and
 * structured external content become real blocks; plain inline paste falls through.
 * Lone YouTube/Vimeo/Loom URLs paste as a link with a Link|Embed chooser in
 * normal blocks; code blocks and the variables table keep the URL as text.
 */
export const useEditorPaste = (blocksRef, commit, editorRootRef, selectedBlockId, setSelectedBlockId) => {
  const [pasteChoice, setPasteChoice] = useState(null);
  const pasteChoiceRef = useRef(null);
  pasteChoiceRef.current = pasteChoice;

  const dismissPasteChoice = useCallback((keepLink = true) => {
    const prev = pasteChoiceRef.current;
    setPasteChoice(null);
    if (!prev) return;
    const root = editorRootRef.current;
    const editable = root?.querySelector?.(
      `[data-block-id="${prev.blockId}"] .doc-editable`
    );
    if (!editable) return;
    const marked = editable.querySelector(`a[${VIDEO_PASTE_MARK}]`);
    if (!marked) return;
    if (keepLink) marked.removeAttribute(VIDEO_PASTE_MARK);
    else marked.remove();
    separateNextUndoStep();
    editable.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        inputType: keepLink ? 'formatRemove' : 'deleteContent',
        data: null,
      })
    );
  }, [editorRootRef]);

  const embedFromPasteChoice = useCallback(() => {
    const prev = pasteChoiceRef.current;
    setPasteChoice(null);
    if (!prev) return;
    const { parsed, blockId } = prev;
    const current = blocksRef.current;
    const block = findBlock(current, blockId);
    if (!block || !parsed) return;

    // Prefer live DOM — coalesced paste commits may lag blocksRef
    const editable = editorRootRef.current?.querySelector?.(
      `[data-block-id="${blockId}"] .doc-editable`
    );
    const liveHtml = editable?.innerHTML ?? block.content ?? '';
    let nextContent = stripVideoPasteChoiceAnchor(liveHtml, {
      removeAnchor: true,
    });
    const url = parsed.originalUrl || '';
    if (url && stripHtml(nextContent).trim() === url) {
      nextContent = '';
    }

    const video = createVideoEmbedBlock(parsed);
    const trailing = createBlock('text');
    const emptyAfterStrip =
      EDITABLE_TYPES.includes(block.type) &&
      stripHtml(nextContent).trim() === '';

    let next = current;
    if (emptyAfterStrip) {
      // Replace the (now empty) host block with the embed — same as the
      // old auto-embed path when pasting into a blank paragraph.
      next = updateBlockInTree(current, blockId, {
        type: 'video',
        content: '',
        props: video.props,
        children: [],
      });
      next = insertBlockAfter(next, blockId, trailing);
    } else {
      next = updateBlockInTree(current, blockId, { content: nextContent });
      next = insertBlockAfter(next, blockId, video);
      next = insertBlockAfter(next, video.id, trailing);
    }
    commit(next);
    setSelectedBlockId(null);
    focusEditable(trailing.id);
  }, [blocksRef, commit, editorRootRef, setSelectedBlockId]);

  // Typing / further edits dismiss the tab and keep the link
  useEffect(() => {
    if (!pasteChoice) return undefined;
    const onKeyDown = (e) => {
      if (e.target.closest?.('.doc-video-paste-choice')) return;
      if (e.key === 'Escape') return; // menu owns Esc
      dismissPasteChoice(true);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [pasteChoice, dismissPasteChoice]);

  useEffect(() => {
    const insertBlocksAt = (newBlocks, target) => {
      const current = blocksRef.current;
      const limited = limitVariablesBlocks(newBlocks, {
        allowOne: !hasVariablesBlock(current),
      });
      if (!limited.length) return;

      const anchorId = target && findBlock(current, target) ? target : null;
      const anchor = anchorId ? findBlock(current, anchorId) : null;

      let next = current;
      let [first, ...rest] = limited;
      let afterId = anchorId;

      if (anchor && anchor.type === 'text' && stripHtml(anchor.content || '').trim() === '') {
        next = updateBlockInTree(current, anchorId, {
          type: first.type,
          content: first.content,
          props: first.props,
          children: first.children,
        });
      } else if (anchorId) {
        next = insertBlockAfter(current, anchorId, first);
        afterId = first.id;
      } else {
        next = [...current, first];
        rest.forEach((b) => {
          next = [...next, b];
        });
        rest = [];
      }
      rest.forEach((b) => {
        next = insertBlockAfter(next, afterId, b);
        afterId = b.id;
      });
      commit(next);

      const lastEditable = [...flattenBlocks(limited)]
        .reverse()
        .find((b) => EDITABLE_TYPES.includes(b.type));
      if (lastEditable) focusEditable(lastEditable.id);
    };

    const onCopy = (e) => {
      const root = editorRootRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (!root.contains(e.target) && !root.contains(active)) return;
      // Only rewrite text selections inside editables — block multi-select
      // copy is handled by useEditorSelection.
      if (!active?.isContentEditable && !e.target?.closest?.('.doc-editable')) return;
      if (writeSelectionToClipboardData(e.clipboardData)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onCut = (e) => {
      const root = editorRootRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (!root.contains(e.target) && !root.contains(active)) return;
      if (!active?.isContentEditable && !e.target?.closest?.('.doc-editable')) return;

      // Rewrite clipboard (keeps <code>/quote fidelity), then delete the
      // selection ourselves — preventDefault would otherwise leave the text.
      if (!writeSelectionToClipboardData(e.clipboardData)) return;
      e.preventDefault();
      e.stopPropagation();

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const anchorEl =
        range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;
      const editable = anchorEl?.closest?.('.doc-editable');
      if (!editable || !root.contains(editable)) return;

      range.deleteContents();
      selection.removeAllRanges();
      selection.addRange(range);

      separateNextUndoStep();
      editable.dispatchEvent(
        new InputEvent('input', { bubbles: true, inputType: 'deleteByCut', data: null })
      );
    };

    const onPaste = (e) => {
      const root = editorRootRef.current;
      if (!root || !root.contains(e.target)) return;

      // Code blocks + raw inputs: verbatim paste (URL stays plain text).
      // Variables table (name textarea OR nested value/description CE): text only.
      if (
        e.target.closest?.('.code-block') ||
        e.target.closest?.('.variables-block') ||
        e.target.closest?.('.variables-panel') ||
        /^(TEXTAREA|INPUT)$/.test(e.target.tagName)
      ) {
        return;
      }

      const caretWrapper = e.target.closest?.('[data-block-id]');
      const anchorId = caretWrapper?.getAttribute('data-block-id') || selectedBlockId;

      // Screenshots / copied image or video files → media blocks, then upload
      const imageFiles = imageFilesFromDataTransfer(e.clipboardData);
      const videoFiles = videoFilesFromDataTransfer(e.clipboardData);
      if (imageFiles.length || videoFiles.length) {
        e.preventDefault();
        e.stopPropagation();
        const mediaItems = [
          ...imageFiles.map((file) => ({
            file,
            block: createLocalImageBlock(file),
            kind: 'image',
          })),
          ...videoFiles.map((file) => ({
            file,
            block: createLocalVideoBlock(file),
            kind: 'video',
          })),
        ];
        const mediaBlocks = mediaItems.map((m) => m.block);
        const current = blocksRef.current;
        const resolvedAnchor =
          anchorId && findBlock(current, anchorId) ? findBlock(current, anchorId) : null;
        const replacedEmpty =
          resolvedAnchor &&
          resolvedAnchor.type === 'text' &&
          stripHtml(resolvedAnchor.content || '').trim() === '';

        insertBlocksAt([...mediaBlocks, createBlock('text')], anchorId);
        setSelectedBlockId(null);
        mediaItems.forEach((item, i) => {
          if (item.block.props?.uploadStatus !== 'local') return;
          const uploadId = i === 0 && replacedEmpty ? resolvedAnchor.id : item.block.id;
          if (item.kind === 'video') startDocVideoUpload(uploadId, item.file);
          else startDocImageUpload(uploadId, item.file);
        });
        return;
      }

      const html = e.clipboardData?.getData('text/html') || '';
      const text = e.clipboardData?.getData('text/plain') || '';

      // Lone YouTube / Vimeo / Loom URL
      if (isSingleVideoUrl(text)) {
        const parsed = parseVideoUrl(text.trim());
        if (parsed) {
          const editable = e.target.closest?.('.doc-editable');
          // Inside a normal editable → paste as link + Link|Embed tab.
          // Table cells get the link only (no embed tab — embeds aren't cells).
          if (editable && root.contains(editable)) {
            e.preventDefault();
            e.stopPropagation();
            setPasteChoice(null);

            const href = parsed.originalUrl || text.trim();
            const linkHtml =
              `<a href="${escapeAttr(href)}" ${VIDEO_PASTE_MARK}="1">` +
              `${escapeHtml(href)}</a>`;

            separateNextUndoStep();
            ensureEditableCaretForInsert();
            document.execCommand('insertHTML', false, linkHtml);
            editable.dispatchEvent(
              new InputEvent('input', {
                bubbles: true,
                inputType: 'insertFromPaste',
                data: null,
              })
            );

            const inTable = !!e.target.closest?.(
              '.table-block, .table-block-wrapper'
            );
            if (!inTable && anchorId) {
              const marked =
                editable.querySelector(`a[${VIDEO_PASTE_MARK}]`) ||
                Array.from(editable.querySelectorAll('a[href]')).find(
                  (a) => a.getAttribute('href') === href
                );
              const rect = marked?.getBoundingClientRect?.();
              setPasteChoice({
                parsed,
                blockId: anchorId,
                href,
                position: {
                  x: rect ? rect.left : 0,
                  y: rect ? rect.bottom + 6 : 0,
                },
              });
            }
            return;
          }

          // Not in an editable (block chrome / empty selection) → embed directly
          e.preventDefault();
          e.stopPropagation();
          insertBlocksAt(
            [createVideoEmbedBlock(parsed), createBlock('text')],
            anchorId
          );
          setSelectedBlockId(null);
          return;
        }
      }

      const pastedBlocks = readBlocksFromClipboardHtml(html);
      if (pastedBlocks) {
        e.preventDefault();
        e.stopPropagation();
        insertBlocksAt(pastedBlocks, anchorId);
        setSelectedBlockId(null);
        return;
      }

      const structured = clipboardToBlocks(html, text);
      if (structured) {
        e.preventDefault();
        e.stopPropagation();
        insertBlocksAt(structured, anchorId);
        setSelectedBlockId(null);
      }
    };
    // Ctrl/Cmd+click opens a link (plain click keeps the caret for editing)
    const onClick = (e) => {
      const root = editorRootRef.current;
      if (!root || !root.contains(e.target)) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const anchor = e.target.closest?.('a[href]');
      if (!anchor || !anchor.closest('.doc-editable')) return;
      const href = (anchor.getAttribute('href') || '').trim();
      if (!/^(https?:\/\/|mailto:)/i.test(href)) return;
      e.preventDefault();
      e.stopPropagation();
      window.open(href, '_blank', 'noopener,noreferrer');
    };

    document.addEventListener('copy', onCopy, true);
    document.addEventListener('cut', onCut, true);
    document.addEventListener('paste', onPaste, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('copy', onCopy, true);
      document.removeEventListener('cut', onCut, true);
      document.removeEventListener('paste', onPaste, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [blocksRef, commit, selectedBlockId, setSelectedBlockId, editorRootRef]);

  const choiceUi = pasteChoice ? (
    <VideoPasteChoice
      position={pasteChoice.position}
      onLink={() => dismissPasteChoice(true)}
      onEmbed={embedFromPasteChoice}
      onClose={() => dismissPasteChoice(true)}
    />
  ) : null;

  return choiceUi;
};
